import Box from '@mui/material/Box';
import { useAppSelector } from '../store/hooks';
import { selectByUploadId, selectUploadIds, selectUploadWorking } from '../store/features/progress/uploadSelectors';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { EntityId } from '@reduxjs/toolkit';
import { green, red } from '@mui/material/colors';
import { RootState } from '../store';
import { useCallback } from 'react';
import { ProgressState } from '../store/features/progress/progressSlicer';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import { BaseProps } from '@mui/material/OverridableComponent';
import { CircularProgress, SvgIconTypeMap } from '@mui/material';
import isNil from 'lodash/isNil';
import { selectEnvironment } from '../store/features/context/selectors';
import { Environment } from '../api/api.type';
import { useBeforeUnload } from 'react-use';

interface UploadProps {
    id: EntityId;
}

const Icon = ({ state, ...props }: { state: ProgressState } & BaseProps<SvgIconTypeMap>) => (
    <Fade in timeout={1000}>
        {state === ProgressState.ERROR ? (
            <ErrorOutlineIcon style={{ color: red[500] }} {...props} />
        ) : (
            <CheckIcon style={{ color: green[500] }} {...props} />
        )}
    </Fade>
);

const Upload = ({ id }: UploadProps) => {
    const selector = useCallback((state: RootState) => selectByUploadId(state, id), [id]);
    const { filename, progress, state } = useAppSelector(selector) || {};

    return (
        <Box padding='8px 0' minWidth='50%' textAlign='center'>
            {filename && (
                <Typography variant='body2' sx={{ position: 'relative', top: 8 }}>
                    {filename}
                </Typography>
            )}
            <Box display='flex' gap={2} alignItems='center'>
                <LinearProgress variant={'determinate'} value={progress ?? 100} sx={{ width: '100%' }} />
                <Box margin='auto' minHeight={35} display='flex' alignItems='center'>
                    {state === ProgressState.DONE || state === ProgressState.ERROR ? (
                        <Icon sx={{ width: 35, height: 35, margin: 'auto' }} state={state} />
                    ) : (
                        <Typography variant='body2' textAlign='right' color='text.secondary'>
                            {isNil(progress) ? (
                                <CircularProgress style={{ height: 30, width: 30 }} />
                            ) : (
                                `${Math.round(progress)}%`
                            )}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export const Uploads = () => {
    const uploadsIds = useAppSelector(selectUploadIds);
    const env = useAppSelector(selectEnvironment);
    const isWorking = useAppSelector(selectUploadWorking);
    useBeforeUnload(isWorking, 'Trwa zapisywanie. Jesli zamkniesz okno możesz utracić dane.');

    return (
        <Box textAlign='center' height='100%' width='100%' display='flex'>
            <Box width='50%' margin='auto'>
                <Typography variant='h4' sx={{ fontSize: 32, lineHeight: '28px' }}>
                    Trwa zapisywanie
                </Typography>
                {env === Environment.DESKTOP && (
                    <Typography
                        variant='h6'
                        sx={{ fontWeight: 400, paddingTop: 1.5, fontSize: 20, lineHeight: '28px' }}
                    >
                        (Okno wyłączy się automatycznie po przesłaniu plików)
                    </Typography>
                )}
                <br />
                {uploadsIds.map(id => (
                    <Upload key={id} id={id} />
                ))}
            </Box>
        </Box>
    );
};
